import _omitBy from 'lodash/omitBy';
import _isNull from 'lodash/isNull';
import _isUndefined from 'lodash/isUndefined';
import _shuffle from 'lodash/shuffle';
import store from 'store2';
import { getAgeData } from '@bdelab/roar-utils';
import jsPsychCallFunction from '@jspsych/plugin-call-function';
import { jsPsych } from '../jsPsych';
import { corpora, loadCorpus } from './corpus';
import { vocabValidityEvaluator } from '../experiment';
import { getUserDataTimeline } from '../trials/getUserData';
import { enter_fullscreen } from '../trials/fullscreen';
import { shuffle } from '../helperFunctions';
import { computedScoreCallback } from '../../scores';

// import i18next from "i18next";

export function makePid() {
  let text = '';
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < 16; i += 1) text += possible.charAt(Math.floor(Math.random() * possible.length));
  return text;
}

export const initStore = () => {
  if (store.session.has('initialized') && store.local('initialized')) {
    return store.session;
  }
  store.session.set('practiceIndex', 0);

  // Counting variables
  store.session.set('practiceIndex', 0);
  store.session.set('currentBlockIndex', 0);
  store.session.set('trialNumBlock', 0); // counter for trials in block
  store.session.set('trialNumTotal', 0); // counter for trials in experiment
  store.session.set('demoCounter', 0);
  store.session.set('targetRespNum', 0); // position number to show the target picture

  store.session.set('corpusTestIndex', 0);
  store.session.set('corpusPracticeIndex', 0);

  store.session.set('nextStimulus', []);
  store.session.set('response', '');

  store.session.set('stimulusRule', 'random');

  store.session.set('ifReplay', 0);

  // variables to track current state of the experiment
  store.session.set('currentStimulus', '');

  store.session.set('currentBlock', '');
  store.session.set('currentTrialCorrect', true); // return true or false

  store.session.set('trialCorrectAns', ''); // for storing the correct answer on a given trial
  store.session.set('coinTrackingIndex', 0);

  store.session.set('totalCorrect', 0);
  store.session.set('totalPercentCorrect', 0);

  store.session.set('initialized', true);
  store.session.set('corpora', corpora);
  store.session.set('corpusPractice', corpora.practice);
  store.session.set('corpusCatch', corpora.catch);
  store.session.set('maxTimeReached', false);

  return store.session;
};

const stimulusRuleLists = {
  fullRandom: ['random', 'random', 'random'], // three random blocks
  shortRandom: ['random', 'random', 'random'], // three random blocks
  multiAFC: ['random', 'random', 'random'],
  test: ['random', 'random', 'random'],
};

const createBlocks = (numTrialsTotal, numBlocks) => {
  // Calculate the size of each block
  const blockSize = Math.ceil(numTrialsTotal / numBlocks);

  // Initialize an array to store the blocks
  const blocks = [];

  // Generate numbers and populate the blocks
  for (let i = 0; i < numBlocks; i += 1) {
    const block = [];
    for (let j = i * blockSize + 1; j <= (i + 1) * blockSize && j <= numTrialsTotal; j += 1) {
      block.push(j);
    }
    blocks.push(block.length);
  }

  return blocks;
};

const stimulusCountLists = (_userMode, _numTrialsTotal) => {
  if (_userMode === 'test') {
    return [3, 3, 3];
  }

  return createBlocks(_numTrialsTotal, 3);
};

function randomNumber(min, max) {
  return Math.floor(Math.random() * (max - min)) + min;
}
const getRandomCatchIndex = (_userMode, _numTrialsTotal) => {
  const stimulusCountList = stimulusCountLists(_userMode, _numTrialsTotal);
  return stimulusCountList.map((count) => randomNumber(1, count));
};

// Stimulus timing options in milliseconds
const stimulusTimeOptions = [null, 350, 1000, 2000];
// Fixation presentation time options in milliseconds
const fixationTimeOptions = [1000, 2000, 25000];
// Trial completion time options in milliseconds
const trialTimeOptions = [null, 5000, 8000, 100000];

export const initConfig = async (firekit, gameParams, userParams, displayElement) => {
  const cleanParams = _omitBy(_omitBy({ ...gameParams, ...userParams }, _isNull), _isUndefined);

  const {
    userMode,
    schoolId,
    sessionId,
    assessmentPid,
    labId,
    recruitment,
    numTrialsTotal = 103,
    userMetadata = {},
    testingOnly,
    consent,
    audioFeedback,
    maxTime,
    // language = i18next.language,
    skipInstructions = false,
    story = true,
    birthMonth,
    birthYear,
    age,
    ageMonths,
    grade,
  } = cleanParams;

  const ageData = getAgeData(birthMonth, birthYear, age, ageMonths);
  // language !== 'en' && i18next.changeLanguage(language)
  const config = {
    userMode: userMode || '4AFC',
    schoolId: schoolId || null,
    pid: assessmentPid,
    labId,
    sessionId: sessionId || null,
    recruitment: recruitment || 'pilot',
    userMetadata: { ...userMetadata, grade, ...ageData },
    testingOnly,
    consent: consent ?? true,
    audioFeedback: audioFeedback || 'binary',
    skipInstructions: skipInstructions || true,
    story: story || false,
    totalTrialsPractice: 5,
    numTrialsTotal: parseInt(numTrialsTotal, 10) || 103,
    stimulusRuleList: stimulusRuleLists[userMode],
    stimulusCountList: stimulusCountLists(userMode, parseInt(numTrialsTotal, 10) || 103),
    randomCatchIndex: getRandomCatchIndex(userMode, parseInt(numTrialsTotal, 10) || 103),
    practiceTrialFormat: userMode === 'multiAFC' ? [2, 2, 3, 3, 4] : [4, 4, 4, 4, 4],
    orderAFC: userMode === 'multiAFC' ? _shuffle([2, 3, 4]) : [4, 4, 4],
    nRandom: 5,
    maxTime: maxTime || null,
    timing: {
      stimulusTimePracticeOnly: stimulusTimeOptions[0], // null as default for practice trial only
      stimulusTime: stimulusTimeOptions[1],
      fixationTime: fixationTimeOptions[0],
      trialTimePracticeOnly: trialTimeOptions[0],
      trialTime: trialTimeOptions[0],
    },
    startTime: new Date(),
    firekit,
    displayElement: displayElement || null,
    corpora: corpora,
    grade,
  };

  const allCorpus = 'vocab-stimuli-v3';
  await loadCorpus(allCorpus);

  if (config.recruitment !== 'garden' && config.recruitment !== 'pilot') {
    const corporaClean = shuffle([...corpora.test_new, ...corpora.test_previous]);
    store.session.set('corpusTest', corporaClean);
  } else {
    store.session.set('corpusTest', corpora.test_previous);
  }

  const updatedGameParams = Object.fromEntries(
    Object.entries(gameParams).map(([key, value]) => [key, config[key] ?? value]),
  );

  await config.firekit.updateTaskParams(updatedGameParams);

  if (config.pid) {
    await config.firekit.updateUser({
      assessmentPid: config.pid,
      ...userMetadata,
    });
  }
  return config;
};

export const initRoarJsPsych = (config) => {
  if (config.displayElement) {
    jsPsych.opts.display_element = config.display_element;
  }

  // Extend jsPsych's on_finish and on_data_update lifecycle functions to mark the
  // run as completed and write data to Firestore, respectively.
  const extend = (fn, code) =>
    function () {
      // eslint-disable-next-line prefer-rest-params
      fn.apply(fn, arguments);
      // eslint-disable-next-line prefer-rest-params
      code.apply(fn, arguments);
    };

  jsPsych.opts.on_finish = extend(jsPsych.opts.on_finish, () => {
    vocabValidityEvaluator.markAsCompleted();
    config.firekit.finishRun();
  });

  jsPsych.opts.on_data_update = extend(jsPsych.opts.on_data_update, (data) => {
    if (data.save_trial) {
      config.firekit.writeTrial(data, computedScoreCallback);
    }
  });

  // Add a special error handler that writes javascript errors to a special trial
  // type in the Firestore database
  window.addEventListener('error', (e) => {
    const { msg, url, lineNo, columnNo, error } = e;

    config.firekit?.writeTrial({
      task: 'error',
      lastTrial: jsPsych.data.getLastTrialData().trials[0],
      message: String(msg),
      source: url || null,
      lineNo: String(lineNo || null),
      colNo: String(columnNo || null),
      error: JSON.stringify(error || null),
      timeStamp: new Date().toISOString(),
    });
  });

  initStore(config);
};

export const initRoarTimeline = (firekit) => {
  // If the participant's ID was **not** supplied through the query string, then
  // ask the user to fill out a form with their ID, class and school.

  const beginningTimeline = [
    ...getUserDataTimeline,
    enter_fullscreen,
    {
      type: jsPsychCallFunction,
      func: () => {
        // eslint-disable-next-line no-param-reassign
        const config = store.session.get('config');
        config.pid = config.pid || makePid();
        firekit.updateUser({ assessmentPid: config.pid, labId: config.labId, ...config.userMetadata });
      },
    },
  ];
  return beginningTimeline;
};
